import React, { useEffect, useState } from 'react';
import Navbar from '../../components/Navbar';
import { useParams } from 'react-router-dom';
import API from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function TeacherResults() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [examDetails, setExamDetails] = useState(null);
  const [liveStats, setLiveStats] = useState({ active: 0, submitted: 0 });
  const { settings } = useAuth();

  const load = () => API.get(`/exams/${id}/results`).then(r => setData(r.data));
  useEffect(() => { load(); }, [id]);

  // Load full exam details (with questions + correct answers) for answer key
  useEffect(() => {
    API.get(`/exams/${id}`).then(r => setExamDetails(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    const fetchLive = () => API.get(`/admin/live-stats/${id}`).then(r => setLiveStats(r.data)).catch(() => {});
    fetchLive();
    const interval = setInterval(fetchLive, 5000);
    return () => clearInterval(interval);
  }, [id]);

  const sendResultWhatsApp = (attempt) => {
    const link = `${window.location.origin}/#/result/${attempt.id}`;
    const msg = `📊 *Exam Result*\n\nStudent: ${attempt.student_name}\nExam: ${data.exam.title}\nScore: ${attempt.score}/${attempt.total_marks}\nPercentage: ${attempt.percentage}%\nRank: #${attempt.rank}\n\nView full result: ${link}`;
    window.open(`https://wa.me/${attempt.student_phone}?text=${encodeURIComponent(msg)}`);
  };

  const shareLeaderboard = () => {
    const link = `${window.location.origin}/#/leaderboard/${data.exam.link_token}`;
    const msg = `🏆 *${data.exam.title} - Leaderboard*\n\nView live rankings: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`);
  };

  const downloadResultsPDF = () => {
    const doc = new jsPDF();
    const brand = settings.brand_name || 'Oexams';
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text(brand, 105, 15, { align: 'center' });
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(settings.tagline || '', 105, 21, { align: 'center' });
    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text(`Results: ${data.exam.title}`, 105, 32, { align: 'center' });
    autoTable(doc, {
      startY: 40,
      head: [['Rank', 'Student', 'Phone', 'Roll No', 'Score', 'Correct', 'Wrong', '%', 'Time']],
      body: data.attempts.map(a => [a.rank, a.student_name, a.student_phone, a.roll_number || '-', `${a.score}/${a.total_marks}`, a.correct_count, a.incorrect_count, `${a.percentage}%`, `${Math.floor((a.time_used || 0) / 60)}m`]),
      styles: { fontSize: 8 }, headStyles: { fillColor: [99, 102, 241] }
    });
    const y = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(8); doc.setTextColor(150);
    doc.text(`${brand} • ${settings.tagline || ''}`, 105, y, { align: 'center' });
    doc.save(`${data.exam.title}_results.pdf`);
  };

  const downloadAnswerKeyPDF = () => {
    if (!examDetails) return;
    const doc = new jsPDF();
    const brand = settings.brand_name || 'Oexams';

    // Header
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text(brand, 105, 15, { align: 'center' });
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(settings.tagline || '', 105, 21, { align: 'center' });
    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text(`Answer Key: ${examDetails.title}`, 105, 32, { align: 'center' });
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(`Subject: ${examDetails.subject || '-'} | Total Questions: ${examDetails.questions.length}`, 105, 39, { align: 'center' });

    // Divider
    doc.setDrawColor(99, 102, 241);
    doc.setLineWidth(0.5);
    doc.line(14, 43, 196, 43);

    let y = 50;
    examDetails.questions.forEach((q, i) => {
      // Check if we need a new page
      if (y > 250) { doc.addPage(); y = 20; }

      // Question number + text
      doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(40, 40, 40);
      const qLines = doc.splitTextToSize(`Q${i + 1}. ${q.question_text}`, 170);
      doc.text(qLines, 14, y);
      y += qLines.length * 5 + 3;

      // Options
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
      q.options.forEach((opt, oi) => {
        if (y > 260) { doc.addPage(); y = 20; }
        const isCorrect = q.correct_answers.includes(opt);
        if (isCorrect) {
          doc.setTextColor(16, 185, 129); // green for correct
          doc.setFont('helvetica', 'bold');
        } else {
          doc.setTextColor(100, 116, 139);
          doc.setFont('helvetica', 'normal');
        }
        const label = String.fromCharCode(65 + oi); // A, B, C, D
        doc.text(`  ${label}. ${isCorrect ? '✓ ' : ''}${opt}`, 18, y);
        y += 5;
      });

      // Stats from question_stats
      const stat = data.question_stats.find(s => s.question_id === q.id);
      if (stat && stat.total > 0) {
        doc.setTextColor(148, 163, 184); doc.setFont('helvetica', 'italic'); doc.setFontSize(8);
        const pct = Math.round((stat.correct_count / stat.total) * 100);
        doc.text(`   Accuracy: ${stat.correct_count}/${stat.total} students correct (${pct}%)`, 18, y);
        y += 5;
      }

      y += 4; // spacing between questions
      // Light separator
      doc.setDrawColor(200, 200, 220); doc.setLineWidth(0.2);
      doc.line(14, y - 1, 196, y - 1);
      y += 2;
    });

    // Footer
    if (y > 270) { doc.addPage(); y = 280; }
    doc.setFontSize(8); doc.setTextColor(150); doc.setFont('helvetica', 'normal');
    doc.text(`${brand} • ${settings.tagline || ''} • Answer Key`, 105, 290, { align: 'center' });

    doc.save(`${examDetails.title}_answer_key.pdf`);
  };

  if (!data) return <div><Navbar /><div className="spinner" style={{ margin: '60px auto' }}></div></div>;
  const avg = data.attempts.length > 0 ? Math.round(data.attempts.reduce((a, x) => a + x.percentage, 0) / data.attempts.length) : 0;

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 20px' }}>
        <div className="page-header flex-between">
          <div><h1>📊 {data.exam.title}</h1><p>{data.exam.subject} • {data.attempts.length} submissions</p></div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={shareLeaderboard}>🏆 Share Leaderboard</button>
            <button className="btn btn-primary" onClick={downloadResultsPDF}>📄 Results PDF</button>
            <button className="btn btn-success" onClick={downloadAnswerKeyPDF} disabled={!examDetails}>🔑 Answer Key PDF</button>
          </div>
        </div>

        {/* Live stats */}
        <div className="card" style={{ marginBottom: 24, borderColor: liveStats.active > 0 ? 'var(--danger)' : 'var(--border)', background: liveStats.active > 0 ? 'rgba(239,68,68,0.05)' : undefined }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: liveStats.active > 0 ? 'var(--danger)' : 'var(--text3)', display: 'inline-block', animation: liveStats.active > 0 ? 'pulse 1.5s infinite' : 'none' }} />
              <span style={{ fontWeight: 700 }}>{liveStats.active} students currently taking exam</span>
            </div>
            <div style={{ color: 'var(--text2)', fontSize: 14 }}>✅ {liveStats.submitted} submitted</div>
            <button onClick={load} className="btn btn-secondary btn-sm">🔄 Refresh</button>
          </div>
        </div>

        <div className="grid-4" style={{ marginBottom: 24 }}>
          {[
            { label: 'Submissions', value: data.attempts.length, icon: '✍️' },
            { label: 'Average %', value: `${avg}%`, icon: '📈' },
            { label: 'Highest', value: data.attempts[0] ? `${data.attempts[0].percentage}%` : '-', icon: '🏆' },
            { label: 'Questions', value: data.question_stats.length, icon: '❓' },
          ].map(s => (
            <div key={s.label} className="card stat-card">
              <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="grid-2">
          <div className="card">
            <h3 style={{ fontWeight: 700, marginBottom: 16 }}>🏆 Rankings</h3>
            {data.attempts.length === 0 ? <p className="text-muted">No submissions yet</p> : (
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Rank</th><th>Name</th><th>Score</th><th>%</th><th>Actions</th></tr></thead>
                  <tbody>
                    {data.attempts.map(a => (
                      <tr key={a.id}>
                        <td><span className={`badge ${a.rank === 1 ? 'badge-warning' : a.rank <= 3 ? 'badge-info' : 'badge-primary'}`}>#{a.rank}</span></td>
                        <td><div style={{ fontWeight: 600 }}>{a.student_name}</div><div style={{ fontSize: 11, color: 'var(--text2)' }}>{a.student_phone}</div></td>
                        <td style={{ fontWeight: 700 }}>{a.score}/{a.total_marks}</td>
                        <td><span className={`badge ${a.percentage >= 80 ? 'badge-success' : a.percentage >= 50 ? 'badge-warning' : 'badge-danger'}`}>{a.percentage}%</span></td>
                        <td><button onClick={() => sendResultWhatsApp(a)} className="btn btn-success btn-sm" title="Send result via WhatsApp">📱</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <h3 style={{ fontWeight: 700, marginBottom: 16 }}>📊 Question Stats</h3>
            {data.question_stats.map((q, i) => {
              const pct = q.total > 0 ? Math.round((q.correct_count / q.total) * 100) : 0;
              return (
                <div key={q.question_id} style={{ marginBottom: 12 }}>
                  <div className="flex-between" style={{ marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>Q{i + 1}: {q.question_text.slice(0, 40)}...</span>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{pct}%</span>
                  </div>
                  <div style={{ height: 8, background: 'var(--card2)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: pct >= 70 ? 'var(--success)' : pct >= 40 ? 'var(--warning)' : 'var(--danger)', borderRadius: 4 }} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{q.correct_count}/{q.total} correct</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}`}</style>
    </div>
  );
}
